import { alpha, createTheme } from '@mui/material/styles';

const palette = {
  mode: 'dark',
  primary: {
    main: '#1cb4d8',
    light: '#52cbe8',
    dark: '#0f95bd',
  },
  secondary: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#b45309',
  },
  success: {
    main: '#22c55e',
  },
  error: {
    main: '#ef4444',
  },
  background: {
    default: '#0b1021',
    paper: alpha('#0f172a', 0.86),
  },
  text: {
    primary: '#e5e7eb',
    secondary: '#94a3b8',
  },
};

const brandGradient = 'linear-gradient(135deg, #1cb4d8 0%, #0ea5e9 25%, #7c3aed 60%, #f59e0b 100%)';

const softShadow = (opacity) => `0 10px 40px ${alpha('#0b1021', opacity)}`;

const theme = createTheme({
  palette,
  shape: {
    borderRadius: 18,
  },
  typography: {
    fontFamily: "'Sora', 'Manrope', 'Inter', system-ui, -apple-system, sans-serif",
    h1: { fontWeight: 700, letterSpacing: '-0.03em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  },
  shadows: [
    'none',
    softShadow(0.28),
    softShadow(0.32),
    softShadow(0.36),
    softShadow(0.4),
    softShadow(0.42),
    softShadow(0.44),
    softShadow(0.46),
    softShadow(0.48),
    softShadow(0.5),
    softShadow(0.52),
    softShadow(0.54),
    softShadow(0.56),
    softShadow(0.58),
    softShadow(0.6),
    softShadow(0.62),
    softShadow(0.64),
    softShadow(0.66),
    softShadow(0.68),
    softShadow(0.7),
    softShadow(0.72),
    softShadow(0.74),
    softShadow(0.76),
    softShadow(0.78),
    softShadow(0.8),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(28, 180, 216, 0.11), transparent 25%), radial-gradient(circle at 80% 0%, rgba(124, 58, 237, 0.2), transparent 28%), radial-gradient(circle at 60% 60%, rgba(245, 158, 11, 0.16), transparent 24%), linear-gradient(145deg, #0b1021 0%, #0f172a 40%, #0a0f1d 100%)',
          color: palette.text.primary,
          minHeight: '100vh',
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-thumb': {
          background: alpha('#1cb4d8', 0.32),
          borderRadius: 20,
        },
        '*::-webkit-scrollbar-track': {
          background: alpha('#0f172a', 0.7),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(120deg, rgba(12, 18, 32, 0.85), rgba(17, 24, 39, 0.78))',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${alpha('#1cb4d8', 0.16)}`,
          boxShadow: softShadow(0.48),
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02))',
          border: `1px solid ${alpha('#1cb4d8', 0.12)}`,
          backdropFilter: 'blur(14px)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${alpha('#1cb4d8', 0.12)}`,
          backgroundImage: 'linear-gradient(145deg, rgba(17, 24, 39, 0.85), rgba(17, 24, 39, 0.75))',
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          paddingInline: 18,
          paddingBlock: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        },
        containedPrimary: {
          backgroundImage: 'linear-gradient(140deg, #1aa4d5 0%, #40c0d9 50%, #7c3aed 100%)',
          color: '#0b1021',
          boxShadow: 'none',
          '&:hover': {
            filter: 'brightness(1.02)',
            boxShadow: '0 10px 26px rgba(26, 164, 213, 0.17)',
          },
        },
        outlined: {
          borderColor: alpha('#1cb4d8', 0.38),
          color: '#e5e7eb',
          '&:hover': {
            backgroundColor: alpha('#1cb4d8', 0.08),
            borderColor: alpha('#1cb4d8', 0.7),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          letterSpacing: '0.01em',
        },
      },
    },
  },
  customGradients: {
    brand: brandGradient,
    glass: 'linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02))',
  },
});

export default theme;
